<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AiAssistantAPI from 'dashboard/api/aiAssistant';

const { t } = useI18n();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const currentChat = useMapGetter('getSelectedChat');
const currentRole = useMapGetter('getCurrentRole');
const isAdmin = computed(() => currentRole.value === 'administrator');

const mode = ref('disabled');
// per-conversation pause: an agent can take over an auto chat without touching
// the inbox setting
const autoPaused = ref(false);
const isTogglingAuto = ref(false);
const logs = ref([]);
const expandedLogId = ref(null);
const isLoadingLogs = ref(false);
const suggestion = ref('');
const isGenerating = ref(false);
const isSending = ref(false);
const errorCode = ref('');
const errorMessage = ref('');
const stats = ref({ totalTokens: null, durationMs: null });
// identifies the generation this suggestion came from, so the backend can
// compare what was suggested against what the agent actually sent
const logId = ref(null);

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const conversationId = computed(() => currentChat.value?.id);

const isPanelOpen = computed(() => Boolean(uiSettings.value.is_ai_panel_open));

const isSuggestionMode = computed(() => mode.value === 'suggestion');
const isAutoMode = computed(() => mode.value === 'auto');

const shouldShowPanel = computed(
  () => isPanelOpen.value && Boolean(conversationId.value)
);

const hasSuggestion = computed(() => suggestion.value.trim().length > 0);

// summary is a briefing for the agent, kept separate from the draft reply so
// it can never be sent to the customer by accident
const summary = ref('');
const isSummarizing = ref(false);
const summaryStats = ref({ totalTokens: null, durationMs: null });
const hasSummary = computed(() => summary.value.trim().length > 0);

const summarize = async () => {
  if (!conversationId.value || isSummarizing.value) return;
  isSummarizing.value = true;
  errorCode.value = '';
  errorMessage.value = '';
  try {
    const { data } = await AiAssistantAPI.summarize(conversationId.value);
    summary.value = data.content || '';
    summaryStats.value = {
      totalTokens: data.total_tokens ?? null,
      durationMs: data.duration_ms ?? null,
    };
  } catch (error) {
    const payload = error?.response?.data || {};
    errorCode.value = payload.error || 'internal_error';
    errorMessage.value = payload.message || '';
    summary.value = '';
  } finally {
    isSummarizing.value = false;
  }
};

const copySummary = async () => {
  try {
    await navigator.clipboard.writeText(summary.value);
    useAlert(t('AI_ASSISTANT.PANEL.SUMMARY_COPIED'));
  } catch (error) {
    useAlert(t('AI_ASSISTANT.PANEL.SEND_ERROR'));
  }
};

const discardSummary = () => {
  summary.value = '';
  summaryStats.value = { totalTokens: null, durationMs: null };
};

const resetState = () => {
  suggestion.value = '';
  errorCode.value = '';
  errorMessage.value = '';
  stats.value = { totalTokens: null, durationMs: null };
  logId.value = null;
  summary.value = '';
  summaryStats.value = { totalTokens: null, durationMs: null };
  logs.value = [];
  expandedLogId.value = null;
};

// only administrators may read the logs: they hold the full context, which can
// include the contact phone number
const fetchLogs = async () => {
  if (!conversationId.value || !isAdmin.value) return;
  isLoadingLogs.value = true;
  try {
    const { data } = await AiAssistantAPI.logs(conversationId.value);
    logs.value = data.payload || [];
  } catch (error) {
    logs.value = [];
  } finally {
    isLoadingLogs.value = false;
  }
};

const fetchStatus = async () => {
  if (!conversationId.value) return;
  try {
    const { data } = await AiAssistantAPI.status(conversationId.value);
    mode.value = data.mode || 'disabled';
    autoPaused.value = Boolean(data.auto_paused);
    if (mode.value === 'auto') fetchLogs();
  } catch (error) {
    mode.value = 'disabled';
  }
};

const toggleAutoPaused = async () => {
  if (!conversationId.value || isTogglingAuto.value) return;
  isTogglingAuto.value = true;
  const next = !autoPaused.value;
  try {
    const { data } = await AiAssistantAPI.setAutoPaused(conversationId.value, next);
    autoPaused.value = Boolean(data.auto_paused);
    useAlert(
      autoPaused.value
        ? t('AI_ASSISTANT.PANEL.AUTO_PAUSED_ON')
        : t('AI_ASSISTANT.PANEL.AUTO_PAUSED_OFF')
    );
  } catch (error) {
    useAlert(t('AI_ASSISTANT.PANEL.SEND_ERROR'));
  } finally {
    isTogglingAuto.value = false;
  }
};

const toggleLog = id => {
  expandedLogId.value = expandedLogId.value === id ? null : id;
};

const logTime = value =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '';

const generate = async () => {
  if (!conversationId.value || isGenerating.value) return;
  isGenerating.value = true;
  errorCode.value = '';
  errorMessage.value = '';
  try {
    const { data } = await AiAssistantAPI.suggest(conversationId.value);
    suggestion.value = data.content || '';
    logId.value = data.log_id ?? null;
    stats.value = {
      totalTokens: data.total_tokens ?? null,
      durationMs: data.duration_ms ?? null,
    };
  } catch (error) {
    const payload = error?.response?.data || {};
    errorCode.value = payload.error || 'internal_error';
    errorMessage.value = payload.message || '';
    suggestion.value = '';
  } finally {
    isGenerating.value = false;
  }
};

const errorLabel = computed(() => {
  if (!errorCode.value) return '';
  const key = `AI_ASSISTANT.PANEL.ERRORS.${errorCode.value}`;
  const translated = t(key);
  return translated === key
    ? errorMessage.value || t('AI_ASSISTANT.PANEL.ERRORS.internal_error')
    : translated;
});

const copy = async () => {
  try {
    await navigator.clipboard.writeText(suggestion.value);
    useAlert(t('AI_ASSISTANT.PANEL.COPIED'));
  } catch (error) {
    useAlert(t('AI_ASSISTANT.PANEL.SEND_ERROR'));
  }
};

const send = async () => {
  if (!hasSuggestion.value || isSending.value) return;
  isSending.value = true;
  const finalText = suggestion.value.trim();
  const sentLogId = logId.value;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: conversationId.value,
      message: finalText,
      private: false,
    });
    // fire and forget: if the agent rewrote the draft, that correction is what
    // teaches the assistant. Never let it get in the way of the message.
    if (sentLogId) {
      AiAssistantAPI.feedback(conversationId.value, sentLogId, finalText).catch(
        () => {}
      );
    }
    useAlert(t('AI_ASSISTANT.PANEL.SENT'));
    resetState();
  } catch (error) {
    useAlert(t('AI_ASSISTANT.PANEL.SEND_ERROR'));
  } finally {
    isSending.value = false;
  }
};

const discard = () => {
  resetState();
};

const closePanel = () => {
  updateUISettings({ is_ai_panel_open: false });
};

const closeOnClickOutside = () => {
  if (isSmallScreen.value && isPanelOpen.value) closePanel();
};

watch(
  conversationId,
  () => {
    resetState();
    fetchStatus();
  },
  { immediate: true }
);

watch(isPanelOpen, open => {
  if (open) fetchStatus();
});
</script>

<template>
  <div
    v-if="shouldShowPanel"
    v-on-click-outside="() => closeOnClickOutside()"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
  >
    <header
      class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-weak shrink-0"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span class="i-lucide-sparkles size-4 text-n-iris-11 shrink-0" />
        <div class="min-w-0">
          <h3 class="text-sm font-medium text-n-slate-12 truncate">
            {{ $t('AI_ASSISTANT.PANEL.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 truncate">
            {{ $t('AI_ASSISTANT.PANEL.SUBTITLE') }}
          </p>
        </div>
      </div>
      <NextButton
        ghost
        slate
        xs
        icon="i-lucide-x"
        class="shrink-0"
        @click="closePanel"
      />
    </header>

    <div class="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
      <!-- summary: a briefing for the agent, never sent to the customer -->
      <div
        v-if="isSummarizing"
        class="flex items-center gap-2 rounded-lg bg-n-alpha-2 px-3 py-3"
      >
        <span class="i-lucide-loader-circle size-4 animate-spin text-n-iris-11" />
        <span class="text-sm text-n-slate-11">
          {{ $t('AI_ASSISTANT.PANEL.SUMMARIZING') }}
        </span>
      </div>

      <div
        v-else-if="hasSummary"
        class="rounded-lg border border-n-weak bg-n-alpha-1 flex flex-col"
      >
        <div
          class="flex items-center justify-between gap-2 px-3 py-2 border-b border-n-weak"
        >
          <span class="flex items-center gap-1.5 text-xs font-medium text-n-slate-12">
            <span class="i-lucide-list-checks size-3.5 text-n-iris-11" />
            {{ $t('AI_ASSISTANT.PANEL.SUMMARY_TITLE') }}
          </span>
          <div class="flex items-center gap-1">
            <NextButton
              v-tooltip.top="$t('AI_ASSISTANT.PANEL.COPY')"
              ghost
              slate
              xs
              icon="i-lucide-clipboard"
              @click="copySummary"
            />
            <NextButton
              v-tooltip.top="$t('AI_ASSISTANT.PANEL.REGENERATE')"
              ghost
              slate
              xs
              icon="i-lucide-refresh-cw"
              @click="summarize"
            />
            <NextButton
              v-tooltip.top="$t('AI_ASSISTANT.PANEL.DISCARD')"
              ghost
              slate
              xs
              icon="i-lucide-x"
              @click="discardSummary"
            />
          </div>
        </div>
        <p
          class="px-3 py-2 text-sm text-n-slate-12 whitespace-pre-wrap break-words"
        >
          {{ summary }}
        </p>
        <span
          v-if="summaryStats.totalTokens"
          class="px-3 pb-2 text-xs text-n-slate-10"
        >
          {{ $t('AI_ASSISTANT.PANEL.TOKENS', { count: summaryStats.totalTokens }) }}
        </span>
      </div>

      <div
        v-if="!isSuggestionMode && !isAutoMode"
        class="flex flex-col items-center text-center gap-2 py-10 px-2"
      >
        <span class="i-lucide-power-off size-6 text-n-slate-10" />
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('AI_ASSISTANT.PANEL.DISABLED_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ $t('AI_ASSISTANT.PANEL.DISABLED_BODY') }}
        </p>
      </div>

      <!-- automatic mode: the agent follows along; admins can audit each run -->
      <template v-else-if="isAutoMode">
        <div
          class="rounded-lg border px-3 py-3 flex items-start gap-2"
          :class="
            autoPaused
              ? 'border-n-amber-6 bg-n-amber-2'
              : 'border-n-weak bg-n-alpha-1'
          "
        >
          <span
            class="size-4 mt-0.5 shrink-0"
            :class="
              autoPaused
                ? 'i-lucide-pause-circle text-n-amber-11'
                : 'i-lucide-bot text-n-iris-11'
            "
          />
          <div class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{
                autoPaused
                  ? $t('AI_ASSISTANT.PANEL.AUTO_PAUSED_TITLE')
                  : $t('AI_ASSISTANT.PANEL.AUTO_TITLE')
              }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{
                autoPaused
                  ? $t('AI_ASSISTANT.PANEL.AUTO_PAUSED_BODY')
                  : $t('AI_ASSISTANT.PANEL.AUTO_BODY')
              }}
            </span>
          </div>
        </div>

        <!-- any agent can pause automatic replies for this chat and take over -->
        <NextButton
          :amber="!autoPaused"
          :teal="autoPaused"
          faded
          sm
          class="w-full"
          :icon="autoPaused ? 'i-lucide-play' : 'i-lucide-pause'"
          :is-loading="isTogglingAuto"
          :disabled="isTogglingAuto"
          :label="
            autoPaused
              ? $t('AI_ASSISTANT.PANEL.AUTO_RESUME')
              : $t('AI_ASSISTANT.PANEL.AUTO_PAUSE')
          "
          @click="toggleAutoPaused"
        />

        <p v-if="!isAdmin" class="text-xs text-n-slate-11">
          {{ $t('AI_ASSISTANT.PANEL.AUTO_ADMIN_ONLY') }}
        </p>

        <template v-else>
          <div class="flex items-center justify-between gap-2">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('AI_ASSISTANT.PANEL.HISTORY') }}
            </span>
            <NextButton
              ghost
              slate
              xs
              icon="i-lucide-refresh-cw"
              :is-loading="isLoadingLogs"
              @click="fetchLogs"
            />
          </div>
          <p v-if="!logs.length" class="text-xs text-n-slate-11">
            {{ $t('AI_ASSISTANT.PANEL.NO_HISTORY') }}
          </p>
          <div
            v-for="log in logs"
            :key="log.id"
            class="rounded-lg border border-n-weak overflow-hidden"
          >
            <button
              class="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-n-alpha-1"
              @click="toggleLog(log.id)"
            >
              <span class="flex flex-col min-w-0">
                <span class="text-xs text-n-slate-12 truncate">
                  {{ logTime(log.created_at) }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ log.model }} · {{ log.total_tokens || 0 }} tokens ·
                  {{ log.duration_ms || 0 }} ms
                </span>
              </span>
              <span
                class="text-xs px-1.5 py-0.5 rounded-full shrink-0"
                :class="
                  log.status === 'success'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-ruby-3 text-n-ruby-11'
                "
              >
                {{ log.status }}
              </span>
            </button>
            <div
              v-if="expandedLogId === log.id"
              class="border-t border-n-weak px-3 py-2 flex flex-col gap-2"
            >
              <div v-if="log.error" class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-ruby-11">
                  {{ $t('AI_ASSISTANT.PANEL.LOG_ERROR') }}
                </span>
                <span class="text-xs text-n-slate-11">{{ log.error }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_ASSISTANT.PANEL.LOG_PROMPT') }}
                </span>
                <pre
                  class="text-xs text-n-slate-11 whitespace-pre-wrap break-words max-h-40 overflow-y-auto"
                  >{{ log.system_prompt }}</pre
                >
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_ASSISTANT.PANEL.LOG_CONTEXT') }}
                </span>
                <pre
                  class="text-xs text-n-slate-11 whitespace-pre-wrap break-words max-h-40 overflow-y-auto"
                  >{{ log.user_context }}</pre
                >
              </div>
              <div v-if="log.response" class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_ASSISTANT.PANEL.LOG_RESPONSE') }}
                </span>
                <pre
                  class="text-xs text-n-slate-12 whitespace-pre-wrap break-words max-h-40 overflow-y-auto"
                  >{{ log.response }}</pre
                >
              </div>
            </div>
          </div>
        </template>
      </template>

      <template v-else>
        <div
          v-if="isGenerating"
          class="flex items-center gap-2 rounded-lg bg-n-alpha-2 px-3 py-3"
        >
          <span class="i-lucide-loader-circle size-4 animate-spin text-n-iris-11" />
          <span class="text-sm text-n-slate-11">
            {{ $t('AI_ASSISTANT.PANEL.GENERATING') }}
          </span>
        </div>

        <div
          v-else-if="errorCode"
          class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-3 py-3 flex flex-col gap-1"
        >
          <span class="text-sm font-medium text-n-ruby-11">
            {{ $t('AI_ASSISTANT.PANEL.ERROR_TITLE') }}
          </span>
          <span class="text-xs text-n-slate-11">{{ errorLabel }}</span>
        </div>

        <div
          v-else-if="!hasSuggestion"
          class="flex flex-col items-center text-center gap-2 py-10 px-2"
        >
          <span class="i-lucide-sparkles size-6 text-n-slate-10" />
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('AI_ASSISTANT.PANEL.EMPTY_TITLE') }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ $t('AI_ASSISTANT.PANEL.EMPTY_BODY') }}
          </p>
        </div>

        <template v-if="hasSuggestion && !isGenerating">
          <textarea
            v-model="suggestion"
            rows="10"
            class="w-full resize-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
          />
          <div
            v-if="stats.totalTokens || stats.durationMs"
            class="flex items-center gap-3 text-xs text-n-slate-10"
          >
            <span v-if="stats.totalTokens">
              {{ $t('AI_ASSISTANT.PANEL.TOKENS', { count: stats.totalTokens }) }}
            </span>
            <span v-if="stats.durationMs">
              {{ $t('AI_ASSISTANT.PANEL.DURATION', { ms: stats.durationMs }) }}
            </span>
          </div>
        </template>
      </template>
    </div>

    <footer
      v-if="isSuggestionMode || isAutoMode"
      class="border-t border-n-weak p-3 flex flex-col gap-2 shrink-0"
    >
      <!-- available in both modes: in automatic the agent only follows along,
           and a briefing is exactly what they need -->
      <NextButton
        slate
        faded
        sm
        class="w-full"
        icon="i-lucide-list-checks"
        :is-loading="isSummarizing"
        :disabled="isSummarizing || isGenerating"
        :label="$t('AI_ASSISTANT.PANEL.SUMMARIZE')"
        @click="summarize"
      />

      <NextButton
        v-if="isSuggestionMode && !hasSuggestion"
        blue
        sm
        class="w-full"
        icon="i-lucide-sparkles"
        :is-loading="isGenerating"
        :disabled="isGenerating"
        :label="$t('AI_ASSISTANT.PANEL.GENERATE')"
        @click="generate"
      />
      <!-- never in automatic mode: there is no draft to send there -->
      <template v-else-if="isSuggestionMode && hasSuggestion">
        <NextButton
          blue
          sm
          class="w-full"
          icon="i-lucide-send"
          :is-loading="isSending"
          :disabled="isSending || isGenerating"
          :label="$t('AI_ASSISTANT.PANEL.SEND')"
          @click="send"
        />
        <!-- icon only: the labels do not fit the 320px panel -->
        <div class="flex items-center justify-center gap-2">
          <NextButton
            v-tooltip.top="$t('AI_ASSISTANT.PANEL.REGENERATE')"
            slate
            faded
            sm
            icon="i-lucide-refresh-cw"
            :disabled="isGenerating || isSending"
            @click="generate"
          />
          <NextButton
            v-tooltip.top="$t('AI_ASSISTANT.PANEL.COPY')"
            slate
            faded
            sm
            icon="i-lucide-clipboard"
            :disabled="isSending"
            @click="copy"
          />
          <NextButton
            v-tooltip.top="$t('AI_ASSISTANT.PANEL.DISCARD')"
            slate
            faded
            sm
            icon="i-lucide-trash-2"
            :disabled="isSending"
            @click="discard"
          />
        </div>
      </template>
    </footer>
  </div>
</template>
